/* eslint-disable react/prop-types */
import React, { useRef, useState } from 'react';

import { Plugin, Template, TemplateConnector, TemplatePlaceholder } from '@devexpress/dx-react-core';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

import QualificationPendingDialog from '../dialog/QualificationPendingDialog';
import QualificationAcceptedDialog from '../dialog/QualificationAcceptedDialog';
import employeeQualificationService from '../../../services/EmployeeQualificationService';
import { isQualificationDataValid, validExpiryDate } from '../helper/helper';
import toast from 'react-hot-toast';
import { Divider } from '@mui/material';

import useQualificationUpload from '../helper/useQualificationUpload';

/* eslint-disable no-shadow */
export const Popup = ({ row, onChange, onApplyChanges, onCancelChanges, open, rows, setEmployeesQualifications }) => {
  const [changeDialogSize, setChangeDialogSize] = useState(false);
  const [changeDialogSizePExisting, setChangeDialogSizePExisting] = useState(false);
  const [changeDialogSizePUpdated, setChangeDialogSizePUpdated] = useState(false);
  const fileInputRef = useRef(null);
  const [isSaving, setIsSaving] = useState(false);
  const { selectFile, cancelConversion, isConverting, fileError } = useQualificationUpload();

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    selectFile(file, (converted) => {
      onChange({
        target: {
          name: 'qualificationUrl',
          value: converted
        }
      });
    });
    event.target.value = '';
  };

  const resetFile = () => {
    // reset the file in the input element
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // reset the file value in the form state
    onChange({
      target: {
        name: 'qualificationUrl',
        value: null
      }
    });
  };

  const handleSave = async () => {
    if (isConverting || fileError || isSaving) return;
    setIsSaving(true);
    try {
      await onApplyChanges();
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelChanges = () => {
    if (isSaving) return;
    cancelConversion();
    onCancelChanges(); // call the grid cancel function
    resetFile(); // reset the file input and state
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancelChanges}
      aria-labelledby="form-dialog-title"
      maxWidth={
        changeDialogSize || changeDialogSizePExisting || changeDialogSizePUpdated || row.status === 'pending'
          ? 'xl'
          : 'md'
      }
      fullWidth
    >
      {row.status === 'pending' ? (
        <>
          <DialogContent>
            <QualificationPendingDialog
              employeeId={row.employeeId}
              qualificationName={row.name}
              setEmployeesQualifications={setEmployeesQualifications}
              rows={rows}
              changeDialogSizePExisting={changeDialogSizePExisting}
              setChangeDialogSizePExisting={setChangeDialogSizePExisting}
              changeDialogSizePUpdated={changeDialogSizePUpdated}
              setChangeDialogSizePUpdated={setChangeDialogSizePUpdated}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={onCancelChanges} color="primary" variant="outlined">
              Cancel
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle id="form-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Qualification Details
            {row?.status?.toLowerCase() === 'expired' && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 10px',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  backgroundColor: '#fee2e2',
                  color: '#dc2626'
                }}
              >
                Expired
              </span>
            )}
          </DialogTitle>
          <DialogContent>
            <QualificationAcceptedDialog
              handleFileChange={handleFileChange}
              handleImageClick={handleImageClick}
              row={row}
              onChange={onChange}
              fileInputRef={fileInputRef}
              changeDialogSize={changeDialogSize}
              setChangeDialogSize={setChangeDialogSize}
            />
          </DialogContent>

          <Divider sx={{ my: 1 }} />

          <DialogActions>
            <Button onClick={handleCancelChanges} disabled={isSaving} color="secondary" variant="outlined">
              Cancel
            </Button>
            {fileError && <span role="alert">{fileError}</span>}
            <Button
              onClick={handleSave}
              disabled={isSaving || isConverting || Boolean(fileError)}
              color="primary"
              variant="contained"
            >
              {isConverting ? 'Converting photo…' : isSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export const PopupEditing = React.memo(({ popupComponent: Popup, setEmployeesQualifications }) => (
  <Plugin>
    <Template name="popupEditing">
      <TemplateConnector>
        {(
          { rows, getRowId, addedRows, editingRowIds, createRowChange, rowChanges },
          { changeRow, changeAddedRow, commitAddedRows, stopEditRows, cancelAddedRows, cancelChangedRows }
        ) => {
          // New qualifications are entered in the grid's inline draft row.
          // The popup editor is only for editing an existing qualification.
          if (editingRowIds.length === 0 || addedRows.length > 0) return null;

          const isNew = addedRows.length > 0;
          let editedRow;
          let rowId;
          if (isNew) {
            rowId = 0;
            editedRow = addedRows[rowId];
          } else {
            [rowId] = editingRowIds;
            const targetRow = rows.filter((row) => getRowId(row) === rowId)[0];
            editedRow = { ...targetRow, ...rowChanges[rowId] };
          }

          const processValueChange = ({ target: { name, value } }) => {
            const changeArgs = {
              rowId,
              change: createRowChange(editedRow, value, name)
            };
            if (isNew) {
              changeAddedRow(changeArgs);
            } else {
              changeRow(changeArgs);
            }
          };

          const rowIds = isNew ? [0] : editingRowIds;
          const applyChanges = () => {
            if (isNew) {
              commitAddedRows({ rowIds });
            } else {
              // stopEditRows({ rowIds });

              //check in helper.js to see the structure of qual in keypay, as backend is directly sending qualificationData to keypay
              let qualificationData = {
                employeeKeyPayId: editedRow?.employeeKeyPayId,
                qualificationId: editedRow?.qualificationId,
                name: editedRow?.name,
                expiryDate: editedRow?.expiryDate || null,
                referenceNumber: editedRow?.referenceNumber,
                issueDate: editedRow?.issueDate || null,
                documentId: editedRow?.documentId || null,
                status: 'accepted'
              };

              //need to validate expiry date, cannot update if it's expired
              const isExpiryDateValid = validExpiryDate(editedRow?.expiryDate);
              if (editedRow?.expiryDate && !isExpiryDateValid) {
                return toast.error('Qualification is already expired');
              }

              //validate data
              const isQualificationValid = isQualificationDataValid(qualificationData);
              if (!isQualificationValid) {
                return toast.error(
                  'Parameter employee keypayId, qualificationId, name, reference number and expiry date or issue date is required'
                );
              }

              let qualificationRecord;
              // eslint-disable-next-line no-undef
              const formData = new FormData();

              // eslint-disable-next-line no-undef
              const userId = localStorage.getItem('userId');
              formData.append('userId', userId);

              if (typeof editedRow?.qualificationUrl === 'string' && editedRow.qualificationUrl.trim() !== '') {
                qualificationRecord = { ...qualificationData, qualificationUrl: editedRow?.qualificationUrl };

                formData.append('qualificationData', JSON.stringify(qualificationRecord));
              } else {
                formData.append('qualificationData', JSON.stringify(qualificationData));
              }

              // eslint-disable-next-line no-undef
              if (editedRow?.qualificationUrl instanceof File) {
                formData.append('file', editedRow?.qualificationUrl);
              }

              return (
                employeeQualificationService
                  .updateEmployeeQualificationInQboByAdmin(formData)
                  // eslint-disable-next-line no-unused-vars
                  .then((data) => {
                    const saved = data?.data?.data;
                    setEmployeesQualifications((current) =>
                      current.map((item) => (getRowId(item) === rowId ? { ...item, ...saved } : item))
                    );
                    stopEditRows({ rowIds });
                    cancelChangedRows({ rowIds });
                    return toast.success("Successfully updated employee's qualification both in qbo ad verd db");
                  })
                  .catch((error) => {
                    // console.log('error is:', error);

                    const errorMessage =
                      error?.response?.data?.message ||
                      error?.response?.message ||
                      error?.message ||
                      "Employee's qualification update failed.";
                    toast.error(errorMessage);
                  })
              );
            }
          };

          const cancelChanges = () => {
            if (isNew) {
              cancelAddedRows({ rowIds });
            } else {
              stopEditRows({ rowIds });
              cancelChangedRows({ rowIds });
            }
          };

          const open = editingRowIds.length > 0;
          return (
            <Popup
              open={open}
              row={editedRow}
              onChange={processValueChange}
              onApplyChanges={applyChanges}
              onCancelChanges={cancelChanges}
              setEmployeesQualifications={setEmployeesQualifications}
              rows={rows}
            />
          );
        }}
      </TemplateConnector>
    </Template>
    <Template name="root">
      <TemplatePlaceholder />
      <TemplatePlaceholder name="popupEditing" />
    </Template>
  </Plugin>
));
PopupEditing.displayName = 'PopupEditing';
